import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import type {
  BarSeriesOption,
  GridComponentOption,
  LegendComponentOption,
  LineSeriesOption,
  TooltipComponentOption,
} from 'echarts';
import type { AppDownloadTrendRow } from './appDownloadApi';

type ECOption = echarts.ComposeOption<
  | LineSeriesOption
  | BarSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | LegendComponentOption
>;

type MetricKey =
  | 'daily_crashes'
  | 'daily_anrs'
  | 'crash_rate'
  | 'anr_rate'
  | 'daily_avg_rating'
  | 'store_listing_visitors'
  | 'store_listing_acquisitions'
  | 'store_listing_conversion_rate';

type SeriesConfig = {
  key: MetricKey;
  label: string;
  color: string;
  type: 'line' | 'bar';
  axis?: 'count' | 'rate' | 'rating';
};

const SERIES_BY_CHART: Record<string, SeriesConfig[]> = {
  quality: [
    { key: 'daily_crashes', label: 'Crashes', color: '#d32f2f', type: 'bar', axis: 'count' },
    { key: 'daily_anrs', label: 'ANRs', color: '#ed6c02', type: 'bar', axis: 'count' },
    { key: 'crash_rate', label: 'Crash rate', color: '#c62828', type: 'line', axis: 'rate' },
    { key: 'anr_rate', label: 'ANR rate', color: '#ef6c00', type: 'line', axis: 'rate' },
  ],
  ratings: [
    { key: 'daily_avg_rating', label: 'Daily avg rating', color: '#6a1b9a', type: 'line', axis: 'rating' },
  ],
  store: [
    {
      key: 'store_listing_visitors',
      label: 'Store visitors',
      color: '#1565c0',
      type: 'line',
      axis: 'count',
    },
    {
      key: 'store_listing_acquisitions',
      label: 'Store acquisitions',
      color: '#2e7d32',
      type: 'line',
      axis: 'count',
    },
    {
      key: 'store_listing_conversion_rate',
      label: 'Conversion rate',
      color: '#00838f',
      type: 'line',
      axis: 'rate',
    },
  ],
};

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  BarChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const formatValue = (value: number | null | undefined, axis?: SeriesConfig['axis']) => {
  if (value == null || Number.isNaN(value)) return '—';
  if (axis === 'rate') return `${(value * 100).toFixed(2)}%`;
  if (axis === 'rating') return value.toFixed(2);
  return Number(value).toLocaleString();
};

const PlayMetricsChart: React.FC<{
  title: string;
  chartKey: keyof typeof SERIES_BY_CHART;
  data: AppDownloadTrendRow[];
}> = ({ title, chartKey, data }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);
  const seriesConfig = SERIES_BY_CHART[chartKey];

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const labels = data.map((row) => row.period);
    const option: ECOption = {
      title: { text: title, left: 0, textStyle: { fontSize: 16, fontWeight: 600 } },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value) => formatValue(Number(value), 'count'),
      },
      legend: { data: seriesConfig.map((item) => item.label), top: 28 },
      grid: { left: 48, right: 48, top: 72, bottom: 40 },
      xAxis: { type: 'category', data: labels },
      yAxis: seriesConfig.some((item) => item.axis === 'rate')
        ? [
            { type: 'value', name: 'Count' },
            {
              type: 'value',
              name: 'Rate',
              axisLabel: { formatter: (value: number) => `${(value * 100).toFixed(1)}%` },
            },
          ]
        : { type: 'value', name: chartKey === 'ratings' ? 'Rating' : 'Count' },
      series: seriesConfig.map((item) => ({
        name: item.label,
        type: item.type,
        yAxisIndex: item.axis === 'rate' ? 1 : 0,
        data: data.map((row) => {
          const value = row[item.key];
          return value == null ? null : Number(value);
        }),
        color: item.color,
        smooth: item.type === 'line',
        connectNulls: false,
      })),
    };

    chartInstance.current.setOption(option, true);
    const resizeHandler = () => chartInstance.current?.resize();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [chartKey, data, seriesConfig, title]);

  return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default PlayMetricsChart;
